export const dantesContent = {
  en: {
    nav: {
      back: "Leon Galt",
      overview: "Overview",
      pricing: "Pricing",
      security: "Security",
      app: "Open App",
    },
    hero: {
      eyebrow: "Dantes by Leongalt",
      title: "Turn messy documents into governed memos, risk flags, debt stacks, and client-ready letters.",
      body: "Dantes is the AI operating system for Quebec real-estate teams working through live files, legal clocks, distressed debt, and bilingual stakeholder communication.",
      primary: "Generate Your First Memo",
      secondary: "See Pricing",
      proof: "Built for investors, brokers, lenders, notaries, and property managers working on real Quebec workflows.",
    },
    workflow: {
      title: "One governed workflow, not two disconnected products.",
      body: "Upload files, launch a snapshot, review the verdict, and keep every artifact inside the same workspace. Chat drives the work. Structured records keep it trustworthy.",
      steps: [
        "Upload JLR profiles, notices, leases, tax records, and underwriting documents.",
        "Run a snapshot that extracts parties, deadlines, debt posture, and high-risk issues.",
        "Promote the result into artifacts: memo, debt stack, risk matrix, profile, and letters.",
      ],
    },
    artifact: {
      kicker: "Artifact-first operating model",
      title: "Every answer becomes something reviewable.",
      body: "Instead of leaving critical analysis trapped in a chat thread, Dantes stores the memo, profile, debt stack, and risk report as first-class artifacts with timestamps, source context, and export-ready formatting.",
      stats: [
        ["Decision surface", "Verdict, debt pressure, deadlines, and next actions in one view"],
        ["Bilingual output", "French and English artifacts that preserve Quebec context"],
        ["Governed exports", "Premium PDFs for internal committees and client delivery"],
      ],
    },
    personas: {
      title: "Built around the operators who actually move the file.",
      items: [
        ["Investor", "Downside-first memos, debt clarity, red flags, and committee-ready exports."],
        ["Broker", "Fast client answers, structured briefs, and fewer blank-chat moments."],
        ["Notary", "Document extraction, referenceable outputs, and cleaner handoffs."],
        ["Lender", "Collateral summaries, borrower risk signals, and faster review loops."],
      ],
    },
    pricing: {
      title: "Pricing aligned with analyses, artifacts, and shared work.",
      subtitle: "Start with proof of value, then expand into a collaborative desk.",
      plans: [
        ["Free", "Proof of value", "5 analyses / month, demo workspace, watermark exports"],
        ["Pro", "Solo operator", "$99/mo, live analyses, clean exports, persistent history"],
        ["Team", "Shared desk", "$299/mo, shared workspaces, deeper usage, collaboration-ready artifacts"],
        ["Enterprise", "Custom rollout", "SSO, audit controls, procurement support, dedicated commitments"],
      ],
      cta: "Full plan comparison",
    },
    security: {
      title: "Trust signals for high-stakes work.",
      items: [
        "Tenant-aware workspaces and authenticated APIs",
        "Rate limiting, billing controls, and export governance",
        "Bilingual UX with explicit \"not legal advice\" boundaries",
        "Roadmap to RBAC, audit logs, SSO, and enterprise controls",
      ],
      link: "Read the security posture",
    },
    footer: {
      disclaimer: "Dantes provides analytical and operational support. It does not provide legal advice.",
      cta: "Book a demo",
    },
    securityPage: {
      title: "Security and trust posture",
      intro: "Dantes is designed for document-heavy, high-stakes real-estate workflows where reliability, auditability, and clear product boundaries matter as much as speed.",
      sections: [
        ["Current controls", "Authenticated APIs, tenant-aware workspaces, rate limiting, billing protections, and export controls designed for public beta."],
        ["Document handling", "Uploads stay attached to the user workspace, artifacts retain provenance, and generated exports preserve the original language and context."],
        ["Enterprise roadmap", "RBAC, immutable audit logging, OIDC-based SSO, and deeper governance controls are planned on the next maturity track."],
      ],
    },
  },
  fr: {
    nav: {
      back: "Leon Galt",
      overview: "Aperçu",
      pricing: "Tarification",
      security: "Sécurité",
      app: "Ouvrir l’application",
    },
    hero: {
      eyebrow: "Dantes par Leongalt",
      title: "Transformez des documents désordonnés en mémos gouvernés, drapeaux de risque, debt stacks et lettres prêtes à livrer.",
      body: "Dantes est le système d’exploitation IA pour les équipes immobilières québécoises qui travaillent sur des dossiers vivants, des délais juridiques, de la dette en détresse et une communication bilingue.",
      primary: "Générer votre premier mémo",
      secondary: "Voir la tarification",
      proof: "Conçu pour les investisseurs, courtiers, prêteurs, notaires et gestionnaires immobiliers qui travaillent sur de vrais dossiers au Québec.",
    },
    workflow: {
      title: "Un seul flux gouverné, pas deux produits déconnectés.",
      body: "Téléversez les fichiers, lancez un snapshot, révisez le verdict et gardez chaque artefact dans le même espace de travail. Le chat orchestre. Les enregistrements structurés rendent le tout fiable.",
      steps: [
        "Téléversez profils JLR, préavis, baux, comptes de taxes et documents d’analyse.",
        "Lancez un snapshot qui extrait les parties, les échéances, la posture de dette et les risques critiques.",
        "Transformez le résultat en artefacts: mémo, debt stack, matrice de risques, profil et lettres.",
      ],
    },
    artifact: {
      kicker: "Modèle orienté artefacts",
      title: "Chaque réponse devient quelque chose qu’on peut réviser.",
      body: "Au lieu de laisser l’analyse critique enfermée dans un fil de discussion, Dantes enregistre le mémo, le profil, le debt stack et le rapport de risques comme des artefacts de premier ordre avec horodatage, provenance et format d’export premium.",
      stats: [
        ["Surface de décision", "Verdict, pression de dette, échéances et prochaines actions dans une seule vue"],
        ["Sorties bilingues", "Artefacts en français et en anglais qui conservent le contexte québécois"],
        ["Exports gouvernés", "PDFs premium pour comités internes et livrables clients"],
      ],
    },
    personas: {
      title: "Construit autour des opérateurs qui font réellement avancer le dossier.",
      items: [
        ["Investisseur", "Mémos orientés downside, clarté sur la dette, drapeaux rouges et exports comité."],
        ["Courtier", "Réponses rapides aux clients, briefs structurés et moins d’anxiété de page blanche."],
        ["Notaire", "Extraction documentaire, sorties traçables et transferts plus propres."],
        ["Prêteur", "Résumés de garanties, signaux de risque emprunteur et revues plus rapides."],
      ],
    },
    pricing: {
      title: "Une tarification alignée sur les analyses, les artefacts et le travail partagé.",
      subtitle: "Commencez par une preuve de valeur, puis passez à un desk collaboratif.",
      plans: [
        ["Free", "Preuve de valeur", "5 analyses / mois, espace de démonstration, exports filigranés"],
        ["Pro", "Opérateur solo", "99 $/mois, analyses réelles, exports propres, historique persistant"],
        ["Team", "Desk partagé", "299 $/mois, espaces partagés, usage accru, artefacts prêts à collaborer"],
        ["Enterprise", "Déploiement sur mesure", "SSO, contrôles d’audit, soutien aux achats, engagements dédiés"],
      ],
      cta: "Comparer tous les plans",
    },
    security: {
      title: "Des signaux de confiance pour un travail à haut enjeu.",
      items: [
        "Espaces de travail tenant-aware et API authentifiées",
        "Rate limiting, contrôles de facturation et gouvernance des exports",
        "UX bilingue avec limites explicites « pas un avis juridique »",
        "Feuille de route vers RBAC, journaux d’audit, SSO et contrôles entreprise",
      ],
      link: "Lire la posture de sécurité",
    },
    footer: {
      disclaimer: "Dantes fournit un soutien analytique et opérationnel. Il ne fournit pas d’avis juridique.",
      cta: "Planifier une démo",
    },
    securityPage: {
      title: "Posture de sécurité et de confiance",
      intro: "Dantes est conçu pour des flux immobiliers riches en documents et à fort enjeu, où la fiabilité, l’auditabilité et des limites produit explicites comptent autant que la vitesse.",
      sections: [
        ["Contrôles actuels", "API authentifiées, espaces de travail tenant-aware, rate limiting, protections de facturation et contrôles d’export pensés pour une bêta publique."],
        ["Gestion documentaire", "Les téléversements restent rattachés à l’espace de travail utilisateur, les artefacts conservent leur provenance et les exports générés préservent la langue et le contexte."],
        ["Feuille de route entreprise", "Le RBAC, les journaux d’audit immuables, le SSO basé sur OIDC et des contrôles de gouvernance plus profonds sont prévus sur la prochaine phase de maturité."],
      ],
    },
  },
};

export function getDantesContent(lang = "en") {
  return dantesContent[lang === "fr" ? "fr" : "en"];
}
